import React, {
    createContext,
    useContext,
    useReducer,
    useEffect,
    useCallback,
    useMemo,
} from 'react';
import axios from 'axios';
import cookie from 'js-cookie';

/**
 * Notification Context for managing notification data and UI state
 * Provides centralized state management for all notification-related operations
 */
const NotificationContext = createContext();

/**
 * Action type constants for the reducer
 */
export const NOTIFICATION_ACTIONS = {
    // Data actions
    FETCH_NOTIFICATIONS_START: 'FETCH_NOTIFICATIONS_START',
    FETCH_NOTIFICATIONS_SUCCESS: 'FETCH_NOTIFICATIONS_SUCCESS',
    FETCH_NOTIFICATIONS_ERROR: 'FETCH_NOTIFICATIONS_ERROR',

    // Load more actions
    LOAD_MORE_START: 'LOAD_MORE_START',
    LOAD_MORE_SUCCESS: 'LOAD_MORE_SUCCESS',
    LOAD_MORE_ERROR: 'LOAD_MORE_ERROR',

    // Preferences actions
    UPDATE_PREFERENCES_START: 'UPDATE_PREFERENCES_START',
    UPDATE_PREFERENCES_SUCCESS: 'UPDATE_PREFERENCES_SUCCESS',
    UPDATE_PREFERENCES_ERROR: 'UPDATE_PREFERENCES_ERROR',

    // UI state actions
    TOGGLE_DROPDOWN: 'TOGGLE_DROPDOWN',
    CLOSE_DROPDOWN: 'CLOSE_DROPDOWN',

    // Polling actions
    SET_POLLING_ENABLED: 'SET_POLLING_ENABLED',

    SET_NOTIFICATIONS: 'SET_NOTIFICATIONS',
    MARK_AS_VIEWED: 'MARK_AS_VIEWED',
    MARK_ALL_AS_VIEWED_START: 'MARK_ALL_AS_VIEWED_START',
    MARK_ALL_AS_VIEWED_SUCCESS: 'MARK_ALL_AS_VIEWED_SUCCESS',
    MARK_ALL_AS_VIEWED_ERROR: 'MARK_ALL_AS_VIEWED_ERROR',
    SET_USER_PREFERENCES: 'SET_USER_PREFERENCES',
    RESET_PAGINATION: 'RESET_PAGINATION',
};

/**
 * Initial state for the notification system
 */
const initialState = {
    // Data
    notifications: [],
    unreadCount: 0,
    preferences: {
        emailEnabled: true,
        inAppEnabled: true,
    },

    // Pagination
    currentPage: 0,
    pageSize: 25,
    totalElements: 0,
    totalPages: 0,
    hasNext: false,
    isLoadingMore: false,

    // UI state
    isDropdownOpen: false,

    // Loading states
    isLoading: false,
    isMarkingAsRead: false,
    isUpdatingPreferences: false,

    // Error states
    error: null,
    preferencesError: null,

    // Polling
    pollingEnabled: true,
    lastFetchTime: null,
};

/**
 * Notification reducer to handle state updates
 */
const notificationReducer = (state, action) => {
    switch (action.type) {
        case NOTIFICATION_ACTIONS.FETCH_NOTIFICATIONS_START:
            return {
                ...state,
                isLoading: true,
                error: null,
            };

        case NOTIFICATION_ACTIONS.FETCH_NOTIFICATIONS_SUCCESS:
            return {
                ...state,
                isLoading: false,
                notifications: action.payload.notifications,
                unreadCount: action.payload.unreadCount,
                currentPage: action.payload.currentPage || 0,
                totalElements: action.payload.totalElements || action.payload.notifications.length,
                totalPages: action.payload.totalPages || 1,
                hasNext: action.payload.hasNext || false,
                lastFetchTime: new Date(),
                error: null,
            };

        case NOTIFICATION_ACTIONS.FETCH_NOTIFICATIONS_ERROR:
            return {
                ...state,
                isLoading: false,
                error: action.payload,
            };

        case NOTIFICATION_ACTIONS.LOAD_MORE_START:
            return {
                ...state,
                isLoadingMore: true,
                error: null,
            };

        case NOTIFICATION_ACTIONS.LOAD_MORE_SUCCESS:
            return {
                ...state,
                isLoadingMore: false,
                notifications: [...state.notifications, ...action.payload.notifications],
                currentPage: action.payload.currentPage,
                totalElements: action.payload.totalElements,
                totalPages: action.payload.totalPages,
                hasNext: action.payload.hasNext,
                error: null,
            };

        case NOTIFICATION_ACTIONS.LOAD_MORE_ERROR:
            return {
                ...state,
                isLoadingMore: false,
                error: action.payload,
            };

        case NOTIFICATION_ACTIONS.RESET_PAGINATION:
            return {
                ...state,
                notifications: [],
                currentPage: 0,
                totalElements: 0,
                totalPages: 0,
                hasNext: false,
            };

        case NOTIFICATION_ACTIONS.UPDATE_PREFERENCES_START:
            return {
                ...state,
                isUpdatingPreferences: true,
                preferencesError: null,
            };

        case NOTIFICATION_ACTIONS.UPDATE_PREFERENCES_SUCCESS:
            return {
                ...state,
                isUpdatingPreferences: false,
                preferences: action.payload,
                preferencesError: null,
            };

        case NOTIFICATION_ACTIONS.UPDATE_PREFERENCES_ERROR:
            return {
                ...state,
                isUpdatingPreferences: false,
                preferencesError: action.payload,
            };

        case NOTIFICATION_ACTIONS.TOGGLE_DROPDOWN:
            return {
                ...state,
                isDropdownOpen: !state.isDropdownOpen,
            };

        case NOTIFICATION_ACTIONS.CLOSE_DROPDOWN:
            return {
                ...state,
                isDropdownOpen: false,
            };

        case NOTIFICATION_ACTIONS.SET_POLLING_ENABLED:
            return {
                ...state,
                pollingEnabled: action.payload,
            };

        case NOTIFICATION_ACTIONS.SET_NOTIFICATIONS:
            return {
                ...state,
                notifications: action.payload,
                unreadCount: action.payload.filter((n) => !n.viewed).length,
            };

        case NOTIFICATION_ACTIONS.MARK_AS_VIEWED:
            return {
                ...state,
                notifications: state.notifications.map((notification) =>
                    action.payload.includes(notification.notificationId)
                        ? { ...notification, viewed: true }
                        : notification
                ),
                unreadCount: Math.max(0, state.unreadCount - action.payload.length),
            };

        case NOTIFICATION_ACTIONS.MARK_ALL_AS_VIEWED_START:
            return {
                ...state,
                isMarkingAsRead: true,
            };

        case NOTIFICATION_ACTIONS.MARK_ALL_AS_VIEWED_SUCCESS:
            return {
                ...state,
                isMarkingAsRead: false,
                notifications: state.notifications.map((notification) => ({
                    ...notification,
                    viewed: true,
                })),
                unreadCount: 0,
            };

        case NOTIFICATION_ACTIONS.MARK_ALL_AS_VIEWED_ERROR:
            return {
                ...state,
                isMarkingAsRead: false,
                error: action.payload,
            };

        case NOTIFICATION_ACTIONS.SET_USER_PREFERENCES:
            return {
                ...state,
                preferences: action.payload,
            };

        default:
            return state;
    }
};

/**
 * Hook to use notification context
 */
export const useNotifications = () => {
    const context = useContext(NotificationContext);
    if (!context) {
        throw new Error('useNotifications must be used within a NotificationProvider');
    }
    return context;
};

/**
 * Provider component for notification system
 */
export function NotificationProvider({ children, projectId }) {
    const [state, dispatch] = useReducer(notificationReducer, initialState);

    // API base URL
    const API_URL = process.env.REACT_APP_API_URL;

    /**
     * Fetch global unread count from API
     */
    const fetchUnreadCount = useCallback(async () => {
        try {
            const xsrfToken = cookie.get('XSRF-TOKEN');
            const userId = cookie.get('userId');

            if (!xsrfToken || !userId) {
                return 0;
            }

            const response = await axios.get(`${API_URL}/notifications/count/unviewed`, {
                withCredentials: true,
                headers: {
                    'X-XSRF-TOKEN': xsrfToken,
                },
            });

            return response.data || 0;
        } catch (error) {
            // Silently handle unread count fetch errors in production
            return 0;
        }
    }, [API_URL]);

    /**
     * Fetch notifications from API - supports pagination
     */
    const fetchNotifications = useCallback(
        async (page = 0, size = 25, reset = true) => {
            try {
                const xsrfToken = cookie.get('XSRF-TOKEN');
                const userId = cookie.get('userId');

                if (!xsrfToken || !userId) {
                    // Silently return if not authenticated - don't dispatch errors
                    return;
                }

                if (reset) {
                    dispatch({ type: NOTIFICATION_ACTIONS.FETCH_NOTIFICATIONS_START });
                } else {
                    dispatch({ type: NOTIFICATION_ACTIONS.LOAD_MORE_START });
                }

                // Fetch notifications and global unread count in parallel
                const [notificationsResponse, globalUnreadCount] = await Promise.all([
                    axios.get(`${API_URL}/notifications`, {
                        params: {
                            page,
                            size,
                        },
                        withCredentials: true,
                        headers: {
                            'X-XSRF-TOKEN': xsrfToken,
                        },
                    }),
                    fetchUnreadCount(),
                ]);

                const actionType = reset
                    ? NOTIFICATION_ACTIONS.FETCH_NOTIFICATIONS_SUCCESS
                    : NOTIFICATION_ACTIONS.LOAD_MORE_SUCCESS;

                const currentPage = notificationsResponse.data.number ?? page;
                const totalPages = notificationsResponse.data.totalPages || 1;
                const hasNext = currentPage + 1 < totalPages; // Calculate hasNext correctly

                dispatch({
                    type: actionType,
                    payload: {
                        notifications: notificationsResponse.data.content,
                        unreadCount: globalUnreadCount,
                        currentPage: notificationsResponse.data.number,
                        totalElements: notificationsResponse.data.totalElements,
                        totalPages: notificationsResponse.data.totalPages,
                        hasNext: hasNext,
                    },
                });
            } catch (error) {
                // Check if it's an authentication error
                if (
                    error.response &&
                    (error.response.status === 401 || error.response.status === 403)
                ) {
                    // Stop polling on authentication errors
                    dispatch({ type: NOTIFICATION_ACTIONS.SET_POLLING_ENABLED, payload: false });
                    // Don't dispatch error to avoid UI disruption on login page
                    return;
                }

                const actionType = reset
                    ? NOTIFICATION_ACTIONS.FETCH_NOTIFICATIONS_ERROR
                    : NOTIFICATION_ACTIONS.LOAD_MORE_ERROR;

                dispatch({
                    type: actionType,
                    payload: error.message || 'Failed to fetch notifications',
                });
            }
        },
        [API_URL, fetchUnreadCount]
    );

    /**
     * Load more notifications (next page)
     */
    const loadMoreNotifications = useCallback(async () => {
        if (state.isLoadingMore || !state.hasNext) {
            return;
        }

        const nextPage = state.currentPage + 1;
        const pageSize = state.pageSize; // Use consistent page size

        await fetchNotifications(nextPage, pageSize, false);
    }, [state.isLoadingMore, state.hasNext, state.currentPage, state.pageSize, fetchNotifications]);

    /**
     * Get notifications filtered by project ID
     */
    const getNotificationsByProject = useCallback(
        (targetProjectId) => {
            if (!targetProjectId) return state.notifications;
            return state.notifications.filter(
                (notification) => notification.projectId === targetProjectId
            );
        },
        [state.notifications]
    );

    /**
     * Get unviewed notification count for a specific project
     */
    const getProjectNotificationCount = useCallback(
        (targetProjectId) => {
            if (!targetProjectId) return 0;
            return state.notifications.filter(
                (notification) => notification.projectId === targetProjectId && !notification.viewed
            ).length;
        },
        [state.notifications]
    );

    /**
     * Get notification counts for multiple projects
     */
    const getMultipleProjectNotificationCounts = useCallback(
        (projectIds) => {
            if (!projectIds || !Array.isArray(projectIds)) return {};

            const countMap = {};
            projectIds.forEach((projectId) => {
                countMap[projectId] = getProjectNotificationCount(projectId);
            });

            return countMap;
        },
        [getProjectNotificationCount]
    );

    /**
     * Mark notifications as viewed
     */
    const markAsViewed = useCallback(
        async (notificationIds) => {
            try {
                const xsrfToken = cookie.get('XSRF-TOKEN');

                if (!xsrfToken) {
                    throw new Error('Authentication required');
                }

                // Backend expects individual POST calls for each notification
                const markPromises = notificationIds.map((id) =>
                    axios.post(
                        `${API_URL}/notifications/${id}/mark-viewed`,
                        {},
                        {
                            withCredentials: true,
                            headers: {
                                'X-XSRF-TOKEN': xsrfToken,
                            },
                        }
                    )
                );

                await Promise.all(markPromises);

                // Update local state
                dispatch({
                    type: NOTIFICATION_ACTIONS.MARK_AS_VIEWED,
                    payload: notificationIds,
                });
            } catch (error) {
                dispatch({
                    type: NOTIFICATION_ACTIONS.FETCH_NOTIFICATIONS_ERROR,
                    payload: error.message || 'Failed to mark notifications as viewed',
                });
            }
        },
        [API_URL]
    );

    /**
     * Mark all notifications as viewed
     */
    const markAllAsViewed = useCallback(async () => {
        try {
            const xsrfToken = cookie.get('XSRF-TOKEN');

            if (!xsrfToken) {
                throw new Error('Authentication required');
            }

            dispatch({ type: NOTIFICATION_ACTIONS.MARK_ALL_AS_VIEWED_START });

            await axios.post(
                `${API_URL}/notifications/mark-all-viewed`,
                {},
                {
                    withCredentials: true,
                    headers: {
                        'X-XSRF-TOKEN': xsrfToken,
                    },
                }
            );

            dispatch({ type: NOTIFICATION_ACTIONS.MARK_ALL_AS_VIEWED_SUCCESS });
        } catch (error) {
            dispatch({
                type: NOTIFICATION_ACTIONS.MARK_ALL_AS_VIEWED_ERROR,
                payload: error.message || 'Failed to mark all notifications as viewed',
            });
        }
    }, [API_URL]);

    /**
     * Fetch user preferences - only when projectId is available
     */
    const fetchUserPreferences = useCallback(async () => {
        if (!projectId) return;

        try {
            const xsrfToken = cookie.get('XSRF-TOKEN');
            const userId = cookie.get('userId');

            if (!xsrfToken || !userId) {
                return;
            }

            const response = await axios.get(
                `${API_URL}/notification-preferences/project/${projectId}`,
                {
                    withCredentials: true,
                    headers: {
                        'X-XSRF-TOKEN': xsrfToken,
                    },
                }
            );

            dispatch({
                type: NOTIFICATION_ACTIONS.SET_USER_PREFERENCES,
                payload: response.data,
            });
        } catch (error) {
            dispatch({
                type: NOTIFICATION_ACTIONS.FETCH_NOTIFICATIONS_ERROR,
                payload: error.message || 'Failed to fetch user preferences',
            });
        }
    }, [projectId, API_URL]);

    /**
     * Update user preferences
     */
    const updateUserPreferences = useCallback(
        async (preferences) => {
            if (!projectId) return;

            try {
                const xsrfToken = cookie.get('XSRF-TOKEN');

                if (!xsrfToken) {
                    throw new Error('Authentication required');
                }

                dispatch({ type: NOTIFICATION_ACTIONS.UPDATE_PREFERENCES_START });

                const response = await axios.put(
                    `${API_URL}/notification-preferences/project/${projectId}`,
                    preferences,
                    {
                        withCredentials: true,
                        headers: {
                            'X-XSRF-TOKEN': xsrfToken,
                            'Content-Type': 'application/json',
                        },
                    }
                );

                dispatch({
                    type: NOTIFICATION_ACTIONS.UPDATE_PREFERENCES_SUCCESS,
                    payload: response.data,
                });
            } catch (error) {
                dispatch({
                    type: NOTIFICATION_ACTIONS.UPDATE_PREFERENCES_ERROR,
                    payload: error.message || 'Failed to update user preferences',
                });
            }
        },
        [projectId, API_URL]
    );

    /**
     * Toggle dropdown visibility
     */
    const toggleDropdown = useCallback(() => {
        dispatch({ type: NOTIFICATION_ACTIONS.TOGGLE_DROPDOWN });
    }, []);

    /**
     * Close dropdown
     */
    const closeDropdown = useCallback(() => {
        dispatch({ type: NOTIFICATION_ACTIONS.CLOSE_DROPDOWN });
    }, []);

    // Initial fetch on mount - only if user is authenticated
    useEffect(() => {
        const xsrfToken = cookie.get('XSRF-TOKEN');
        const userId = cookie.get('userId');

        if (xsrfToken && userId) {
            fetchNotifications();
            // Also fetch preferences if we have a projectId
            if (projectId) {
                fetchUserPreferences();
            }
        }
    }, [fetchNotifications, fetchUserPreferences, projectId]);

    // Polling effect - only poll if user is authenticated and polling is enabled
    useEffect(() => {
        const xsrfToken = cookie.get('XSRF-TOKEN');
        const userId = cookie.get('userId');

        if (!state.pollingEnabled || !xsrfToken || !userId) {
            return;
        }

        const interval = setInterval(() => {
            // Double-check authentication before each poll
            const currentXsrfToken = cookie.get('XSRF-TOKEN');
            const currentUserId = cookie.get('userId');

            if (currentXsrfToken && currentUserId) {
                fetchNotifications();
            } else {
                // Stop polling if authentication is lost
                dispatch({ type: NOTIFICATION_ACTIONS.SET_POLLING_ENABLED, payload: false });
            }
        }, 30000); // Poll every 30 seconds

        return () => clearInterval(interval);
    }, [state.pollingEnabled, fetchNotifications]);

    // Notifications sorted newest-first for display (single unified list)
    const sortedNotifications = useMemo(() => {
        return [...state.notifications].sort((a, b) => {
            // Backend now sends proper UTC LocalDateTime, add 'Z' if needed
            const aTimeStr = (a.createdAt || a.timestamp).toString();
            const bTimeStr = (b.createdAt || b.timestamp).toString();

            const aTime = new Date(aTimeStr.endsWith('Z') ? aTimeStr : aTimeStr + 'Z');
            const bTime = new Date(bTimeStr.endsWith('Z') ? bTimeStr : bTimeStr + 'Z');

            return bTime - aTime;
        });
    }, [state.notifications]);

    const value = {
        // State
        notifications: state.notifications,
        sortedNotifications,
        loading: state.isLoading,
        isLoadingMore: state.isLoadingMore,
        error: state.error,
        preferences: state.preferences,
        preferencesError: state.preferencesError,
        isUpdatingPreferences: state.isUpdatingPreferences,
        currentProjectId: projectId,

        // Pagination
        currentPage: state.currentPage,
        totalElements: state.totalElements,
        totalPages: state.totalPages,
        hasNext: state.hasNext,
        pageSize: state.pageSize,

        // Computed
        unreadCount: state.unreadCount,
        isDropdownOpen: state.isDropdownOpen,

        // Actions
        fetchNotifications,
        loadMoreNotifications,
        markAsViewed,
        markAllAsViewed,
        fetchUserPreferences,
        updatePreferences: updateUserPreferences,
        toggleDropdown,
        closeDropdown,
        clearError: () =>
            dispatch({ type: NOTIFICATION_ACTIONS.FETCH_NOTIFICATIONS_ERROR, payload: null }),

        // Utilities
        getNotificationsByProject,
        getProjectNotificationCount,
        getMultipleProjectNotificationCounts,
    };

    return <NotificationContext.Provider value={value}>{children}</NotificationContext.Provider>;
}

export default NotificationContext;
